/**
 * Layout Provider Context
 * Provides layout state and configuration throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  getLayoutConfig,
  getThemeConfig,
  getResponsiveSidebarConfig,
  getLayoutStyles,
  getNavigationConfig,
  getScreenSizeCategory,
} from '../config/layoutConfig';
import { PORTAL_TYPES } from '../types/layoutTypes';

const LayoutContext = createContext(null);

/**
 * Determine portal type from current route
 * @param {string} pathname - Current route pathname
 * @returns {string} Portal type
 */
const getPortalTypeFromRoute = (pathname) => {
  if (pathname.startsWith('/admin')) return PORTAL_TYPES.ADMIN;
  if (pathname.startsWith('/client')) return PORTAL_TYPES.CLIENT;
  if (pathname.startsWith('/super-admin')) return PORTAL_TYPES.SUPER_ADMIN;
  return PORTAL_TYPES.BUREAU; // Default to bureau
};

/**
 * Layout Provider Component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @param {string} props.portalType - Override portal type
 * @param {Object} props.customConfig - Custom layout configuration
 */
export const LayoutProvider = ({ children, portalType: overridePortalType, customConfig = {} }) => {
  const theme = useTheme();
  const location = useLocation();

  // Desktop-focused responsive breakpoints
  const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isMonitor = useMediaQuery(theme.breakpoints.up('xl'));
  const isDark = theme.palette.mode === 'dark';

  // Get current screen size category
  const screenSize = useMemo(() => {
    if (isMonitor) return 'monitor';
    if (isDesktop) return 'desktop';
    return 'laptop';
  }, [isLaptop, isDesktop, isMonitor]);

  // Determine portal type
  const portalType = overridePortalType || getPortalTypeFromRoute(location.pathname);

  // Layout state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [pageTitle, setPageTitle] = useState('');
  const [headerActions, setHeaderActions] = useState([]);

  // Get configurations
  const layoutConfig = useMemo(() => {
    const baseConfig = getLayoutConfig(portalType);
    return { ...baseConfig, ...customConfig, portalType };
  }, [portalType, customConfig]);

  const themeConfig = useMemo(() => {
    return getThemeConfig(portalType);
  }, [portalType]);

  const navigationConfig = useMemo(() => {
    return getNavigationConfig(portalType);
  }, [portalType]);

  const sidebarConfig = useMemo(() => {
    return getResponsiveSidebarConfig(layoutConfig, screenSize);
  }, [layoutConfig, screenSize]);

  const layoutStyles = useMemo(() => {
    return getLayoutStyles(layoutConfig, theme, isDark, screenSize);
  }, [layoutConfig, theme, isDark, screenSize]);

  // Handle desktop responsive sidebar behavior
  useEffect(() => {
    // Always keep sidebar open by default on desktop
    setSidebarOpen(sidebarConfig.defaultOpen);
    // Adjust collapsed state based on screen size
    if (screenSize === 'laptop' && layoutConfig.sidebarCollapsible) {
      setSidebarCollapsed(true); // Start collapsed on smaller laptops
    } else {
      setSidebarCollapsed(false); // Expanded on desktop and monitor
    }
  }, [sidebarConfig.defaultOpen, screenSize, layoutConfig.sidebarCollapsible]);

  // Layout actions optimized for desktop
  const toggleSidebar = () => {
    if (sidebarConfig.floating) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const openSidebar = () => {
    setSidebarOpen(true);
    if (!sidebarConfig.floating) {
      setSidebarCollapsed(false);
    }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const updateBreadcrumbs = (newBreadcrumbs) => {
    setBreadcrumbs(newBreadcrumbs);
  };

  const updatePageTitle = (title) => {
    setPageTitle(title);
  };

  const updateHeaderActions = (actions) => {
    setHeaderActions(actions);
  };

  // Context value
  const value = {
    // Configuration
    layoutConfig,
    themeConfig,
    navigationConfig,
    sidebarConfig,
    layoutStyles,

    // State
    portalType,
    sidebarOpen,
    sidebarCollapsed,
    breadcrumbs,
    pageTitle,
    headerActions,

    // Desktop responsive flags
    isLaptop,
    isDesktop,
    isMonitor,
    screenSize,
    isDark,

    // Actions
    toggleSidebar,
    openSidebar,
    closeSidebar,
    updateBreadcrumbs,
    updatePageTitle,
    updateHeaderActions,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

/**
 * Hook to use layout context
 * @returns {Object} Layout context value
 */
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

/**
 * Hook to use layout configuration only
 * @returns {Object} Layout configuration
 */
export const useLayoutConfig = () => {
  const { layoutConfig } = useLayout();
  return layoutConfig;
};

/**
 * Hook to use sidebar state and actions
 * @returns {Object} Sidebar state and actions
 */
export const useSidebar = () => {
  const { sidebarOpen, sidebarCollapsed, sidebarConfig, toggleSidebar, openSidebar, closeSidebar } =
    useLayout();

  return {
    isOpen: sidebarOpen,
    isCollapsed: sidebarCollapsed,
    config: sidebarConfig,
    toggle: toggleSidebar,
    open: openSidebar,
    close: closeSidebar,
  };
};

/**
 * Hook to use breadcrumb functionality
 * @returns {Object} Breadcrumb state and actions
 */
export const useBreadcrumbs = () => {
  const { breadcrumbs, updateBreadcrumbs } = useLayout();

  return {
    breadcrumbs,
    setBreadcrumbs: updateBreadcrumbs,
  };
};

export default LayoutProvider;
